import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, UtensilsCrossed, Coffee, Palmtree, Package, Building } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SEO from "@/components/SEO";

const SERVE = [
  { icon: Building, label: "Hotels" },
  { icon: UtensilsCrossed, label: "Restaurants" },
  { icon: Coffee, label: "Cafés" },
  { icon: Palmtree, label: "Resorts & Spas" },
  { icon: Package, label: "Catering businesses" },
  { icon: Building2, label: "Corporate hospitality" },
];

const CLIENT_MARQUEE = [
  "Boutique Hotels",
  "Fine Dining",
  "Specialty Cafés",
  "Resort Groups",
  "Corporate Catering",
  "Country Clubs",
  "Michelin Kitchens",
  "Airline Lounges",
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 5)))
      .catch(() => setFeatured([]));
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Cake World USA",
          url: "https://cakeworldusa.com",
          logo: "https://customer-assets-7cd3h4nn.emergentagent.net/job_bakery-b2b/artifacts/aft2loce_183.jpg",
          description:
            "Wholesale premium desserts for hotels, restaurants and cafés — nationwide cold-chain delivery.",
        }}
      />
      {/* HERO — editorial, hospitality-focused, no product shot */}
      <section className="relative bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-24 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="flex items-center gap-3 text-brand-red">
              <span className="w-8 h-px bg-brand-red" />
              <span className="text-[11px] tracking-[0.32em] uppercase font-ui font-bold">B2B Wholesale · Est. Cake World</span>
            </div>
            <h1 className="mt-6 font-display text-[3rem] sm:text-[4.25rem] lg:text-[5.5rem] leading-[0.98] tracking-tight text-brand-ink">
              Wholesale desserts,<br/>
              plated for<br/>
              <span className="italic font-normal text-brand-red">hospitality.</span>
            </h1>
            <p className="mt-8 text-lg text-brand-stone max-w-xl leading-relaxed font-ui">
              Cake World partners with hotels, restaurants and cafés to deliver a premium signature dessert program — without the overhead of in-house pastry production.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/catalog"
                data-testid="hero-catalog"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-ink text-white text-sm font-ui font-semibold hover:bg-brand-red active:scale-95 transition"
              >
                Explore the collection <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="https://cakeworldusa.com/book-a-meeting/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-meeting"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border border-brand-ink text-brand-ink text-sm font-ui font-semibold hover:bg-brand-ink hover:text-white transition"
              >
                Book a B2B meeting
              </a>
            </div>

            {/* Trust bar */}
            <div className="mt-12 pt-8 border-t border-brand-line grid grid-cols-3 gap-6 max-w-xl">
              {[
                { k: "60+", v: "Hospitality accounts" },
                { k: "48 states", v: "Cold-chain delivery" },
                { k: "1 day", v: "Quote turnaround" },
              ].map((s) => (
                <div key={s.k}>
                  <div className="font-display text-3xl text-brand-ink">{s.k}</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase text-brand-stone font-ui font-semibold mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up-delay-2">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] shadow-soft-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_bakery-b2b/artifacts/5ldemjet_Captura%20de%20pantalla%202025-12-13%20a%20la%28s%29%208.59.26%E2%80%AFp.%C2%A0m..png"
                alt="Cake World dessert display — pink tablescape"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent to-transparent" />
            </div>
            {/* Editorial signature format card */}
            <div className="hidden md:block absolute -left-6 bottom-8 bg-white border border-brand-line rounded-2xl shadow-soft-lg p-5 max-w-[240px]">
              <div className="eyebrow">Signature format</div>
              <div className="mt-2 font-display text-2xl text-brand-ink leading-tight">3″ Mini · 10″ Whole</div>
              <div className="mt-1 text-xs text-brand-stone font-ui">Two hospitality-first sizes.</div>
            </div>
          </div>
        </div>

        {/* Client marquee */}
        <div className="border-y border-brand-line bg-brand-pinkSoft">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center gap-8 overflow-x-auto scrollbar-none">
            <div className="text-[10px] tracking-[0.32em] uppercase text-brand-stone font-ui font-bold shrink-0">Trusted by</div>
            {CLIENT_MARQUEE.map((c, i) => (
              <React.Fragment key={c}>
                {i > 0 && <span className="text-brand-pink shrink-0">✦</span>}
                <span className="font-display italic text-lg text-brand-ink shrink-0 whitespace-nowrap">{c}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL STATEMENT */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-28 text-center">
        <div className="eyebrow">The Cake World promise</div>
        <blockquote className="mt-8 font-display text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight">
          "Every occasion needs a <span className="italic text-brand-red">sweet touch</span> — the kind hospitality guests remember, and general managers put on the menu again."
        </blockquote>
        <div className="mt-10 inline-flex items-center gap-3 text-brand-stone">
          <span className="w-8 h-px bg-brand-red" />
          <span className="text-[11px] tracking-[0.32em] uppercase font-ui font-bold">A hospitality partner, not a supplier</span>
          <span className="w-8 h-px bg-brand-red" />
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="bg-brand-pinkSoft border-y border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-2xl">
            <div className="eyebrow">Who we serve</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-brand-ink leading-tight">Built for hospitality operations at every scale.</h2>
            <p className="mt-5 text-brand-stone font-ui">
              From single-property boutique hotels to multi-location restaurant groups — the same signature-quality dessert program, tailored to your operation.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {SERVE.map((s, i) => (
              <div key={s.label} data-testid={`serve-${i}`} className="flex flex-col items-center text-center gap-3 group bg-white rounded-2xl border border-brand-line p-6 hover:border-brand-red transition">
                <div className="w-14 h-14 rounded-full bg-brand-pinkLight text-brand-red grid place-items-center group-hover:bg-brand-red group-hover:text-white transition">
                  <s.icon className="w-6 h-6" />
                </div>
                <div className="text-sm font-ui text-brand-ink font-semibold">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED — reframed as "The Collection" */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28">
        <div className="grid md:grid-cols-2 gap-10 items-end mb-14">
          <div>
            <div className="eyebrow">The collection</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-brand-ink leading-tight">
              Five signature desserts,<br/>two hospitality formats.
            </h2>
          </div>
          <p className="text-brand-stone font-ui md:text-right">
            Every reference below is available in a 3″ Mini Cake for individual plating and a 10″ Whole Cake for share service. Add them to your quote cart to receive tailored wholesale pricing.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {featured.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>

        <div className="mt-12 text-center">
          <Link to="/catalog" data-testid="home-view-all" className="inline-flex items-center gap-2 text-sm font-ui font-semibold text-brand-red hover:text-brand-redDark">
            See the full selection <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* SAMPLE BOX TEASER */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
        <div className="rounded-[2rem] bg-brand-pinkLight border border-brand-line overflow-hidden grid md:grid-cols-2 gap-0">
          <div className="p-10 md:p-14 flex flex-col justify-center">
            <div className="eyebrow">Sample Box · B2B only</div>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl text-brand-ink leading-[1.05]">
              Taste before you<br/>
              <span className="italic text-brand-red">commit.</span>
            </h2>
            <p className="mt-5 text-brand-stone font-ui">
              Request a curated sample box with our five signature mini cakes plus the full product catalog, wholesale pricing and menu-planning guides. Reserved for verified hospitality businesses.
            </p>
            <ul className="mt-6 grid grid-cols-2 gap-2 text-sm font-ui text-brand-ink">
              {["Karpatka", "Tiramisu", "NY Cheesecake", "Chajá", "Chocolate", "+ Catalog & pricing"].map((x) => (
                <li key={x} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-red" /> {x}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Link
                to="/sample-box"
                data-testid="home-sample-box"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-ink text-white text-sm font-ui font-semibold hover:bg-brand-red transition"
              >
                Request your sample box <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="relative min-h-[320px] md:min-h-full bg-white">
            <img
              src="https://customer-assets.emergentagent.com/job_bakery-b2b/artifacts/du6yg2yy_ChatGPT%20Image%2011%20mar%202026%2C%2002_24_24%20p.m..png"
              alt="Cake World sample box with five mini cakes"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* PARTNER TRACKS */}
      <section className="bg-brand-ink text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24">
          <div className="max-w-2xl mb-14">
            <div className="eyebrow text-brand-pink">Partnerships</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl leading-tight">Two ways to grow with Cake World.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                tag: "For manufacturing partners",
                bullets: [
                  "Private label collaboration",
                  "Consistent production opportunities",
                  "Long-term B2B growth",
                  "Premium dessert portfolio",
                ],
              },
              {
                tag: "For distribution partners",
                bullets: [
                  "Cold-chain distribution opportunities",
                  "Hospitality-focused routes",
                  "Regional expansion potential",
                  "Reliable reorder model",
                ],
              },
            ].map((p, i) => (
              <div key={p.tag} data-testid={`partner-track-${i}`} className="bg-white/5 border border-white/10 rounded-3xl p-8 md:p-10">
                <div className="eyebrow text-brand-pink">{p.tag}</div>
                <ul className="mt-6 space-y-3 text-sm font-ui text-white/90">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-brand-pink mt-0.5 shrink-0" /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/partnerships" className="mt-8 inline-flex items-center gap-2 text-sm font-ui font-semibold text-brand-pink hover:text-white">
                  Learn more <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-28">
        <div className="max-w-2xl mx-auto text-center">
          <div className="eyebrow">How it works</div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl text-brand-ink leading-tight">From catalog to first delivery in days.</h2>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-8 relative">
          {[
            { n: "01", title: "Browse the collection", body: "Explore our signature desserts in both 3″ Mini and 10″ Whole formats." },
            { n: "02", title: "Build your quote cart", body: "Add products and quantities — no account required." },
            { n: "03", title: "Share your details", body: "Business info, volume and delivery window in a single form." },
            { n: "04", title: "Receive your quote", body: "Personalized pricing and lead time within one business day." },
          ].map((s, i) => (
            <div key={s.n} data-testid={`how-step-${s.n}`} className="relative">
              <div className="font-display text-7xl text-brand-pink leading-none">{s.n}</div>
              <div className="mt-4 w-10 h-px bg-brand-red" />
              <h3 className="mt-4 font-display text-2xl text-brand-ink leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm text-brand-stone leading-relaxed font-ui">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY CAKE WORLD */}
      <section className="bg-brand-pinkSoft border-y border-brand-line">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow">Why Cake World</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl text-brand-ink leading-tight">A premium dessert program without the overhead.</h2>
            <p className="mt-6 text-brand-stone font-ui">
              We handle the recipes, production and delivery. Your team handles service.
            </p>
          </div>
          <ul className="md:col-span-7 grid sm:grid-cols-2 gap-4 text-sm font-ui text-brand-ink">
            {[
              "Premium desserts without in-house pastry production",
              "Consistent quality across every order",
              "Elegant presentation for upscale venues",
              "Scalable for multi-location operations",
              "Efficient sourcing via our partner network",
              "Fully B2B focused — hospitality only",
            ].map((x) => (
              <li key={x} className="flex items-start gap-3 bg-white rounded-2xl border border-brand-line p-4">
                <Check className="w-5 h-5 text-brand-red mt-0.5 shrink-0" /> <span>{x}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="rounded-[2rem] bg-brand-ink text-white p-12 md:p-20 relative overflow-hidden text-center">
            <div className="eyebrow text-brand-pink">Ready to get started?</div>
            <h2 className="mt-4 font-display text-4xl sm:text-6xl leading-[1.05] tracking-tight">
              Bring Cake World<br/>
              <span className="italic text-brand-pink">to your menu.</span>
            </h2>
            <p className="mt-6 text-white/75 max-w-xl mx-auto font-ui">
              Tell us about your business — we'll return a personalized wholesale quote within one business day.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link
                to="/request-quote"
                data-testid="cta-request-quote"
                className="inline-flex items-center gap-2 h-14 px-8 rounded-full bg-brand-red text-white text-base font-ui font-semibold hover:bg-brand-redDark active:scale-95 transition"
              >
                Request wholesale quote <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="https://cakeworldusa.com/book-a-meeting/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-14 px-8 rounded-full border border-white/30 text-white text-base font-ui font-semibold hover:bg-white hover:text-brand-ink transition"
              >
                Book a meeting
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
